import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MdUploadFile, MdClose } from 'react-icons/md';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

// ── Shared File Selection Warning Dialog ────────────────────────────────────
export interface FileSelectionWarningDialogProps {
  title?: string | null;
  subtitle?: string | null;
  message: string | null;
  zIndex?: number;
  onClose: () => void;
}

export function FileSelectionWarningDialog({
  title,
  subtitle,
  message,
  zIndex = 19000,
  onClose,
}: FileSelectionWarningDialogProps) {
  if (!message) return null;
  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      style={{ zIndex }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="flex flex-col gap-4 p-6 rounded-lg border border-[var(--color-accent)] bg-[var(--color-secondary)] text-[var(--color-text)] shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
        role="alert"
        aria-live="assertive"
        aria-label="File selection warning"
      >
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="7" x2="12" y2="13" />
              <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-1">{title ?? 'File Selection Warning'}</h3>
            {subtitle !== null && subtitle !== undefined && <p className="text-sm opacity-80">{subtitle}</p>}
          </div>
        </div>
        {/* Warning message */}
        <div className="warning-message-box p-3 rounded text-sm whitespace-pre-line bg-red-100 text-red-800">
          {message}
        </div>
        {/* Action button */}
        <div className="flex justify-end">
          <button
            type="button"
            className="px-4 py-2 text-sm rounded bg-[var(--color-accent)] text-white hover:opacity-80"
            onClick={onClose}
          >
            Got It
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
// ────────────────────────────────────────────────────────────────────────────

interface FileDropZoneProps {
  isBusy: boolean;
  isDragging: boolean;
  label: string;
  onDropzoneClick: () => void;
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

function FileDropZone({
  isBusy,
  isDragging,
  label,
  onDropzoneClick,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
}: FileDropZoneProps) {
  return (
    <div
      onClick={isBusy ? undefined : onDropzoneClick}
      onDragEnter={isBusy ? undefined : onDragEnter}
      onDragOver={isBusy ? undefined : onDragOver}
      onDragLeave={isBusy ? undefined : onDragLeave}
      onDrop={isBusy ? undefined : onDrop}
      className={`border-2 border-dashed rounded-lg p-8 mb-6 cursor-pointer transition-all ${
        isDragging
          ? 'border-[var(--color-accent)] bg-[var(--color-accent)] bg-opacity-10'
          : isBusy
            ? 'border-[var(--border)] opacity-60 cursor-not-allowed'
            : 'border-[var(--border)] hover:border-[var(--color-accent)] hover:bg-[var(--hover)]'
      }`}
      aria-disabled={isBusy}
    >
      <motion.div
        initial={false}
        animate={{ opacity: isBusy ? 0.65 : 1, scale: isBusy ? 0.99 : 1 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        className="flex flex-col items-center justify-center gap-3"
      >
        {React.createElement(MdUploadFile as any, { className: 'w-12 h-12 text-[var(--color-text)]' })}
        <p className="font-bold text-[var(--color-text)] text-center">{label}</p>
      </motion.div>
    </div>
  );
}

interface FileUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: () => void;
  onDropzoneClick: () => void;
  uploadedFiles: Array<{ name: string; status?: string; type?: string }>;
  isUploading: boolean;
  isDataAnalysisMode?: boolean;
  onRemoveFile?: (fileName: string) => void;
  onCancelFileRead?: () => void;
  awaitingFileSelection?: boolean;
  fileWarning?: string | null;
  fileWarningTitle?: string | null;
  onClearFileWarning?: () => void;
  /** Called with validated File[] after drop — parent handles processing & validation */
  onFilesDropped?: (files: File[]) => void;
  statusOnly?: boolean;
  hideDropzone?: boolean;
}

export function FileUploadDialog({ 
  isOpen, 
  onClose, 
  onUpload, 
  onDropzoneClick,
  uploadedFiles,
  isUploading,
  isDataAnalysisMode = false,
  onRemoveFile,
  onCancelFileRead,
  awaitingFileSelection,
  fileWarning,
  fileWarningTitle,
  onClearFileWarning,
  onFilesDropped,
  statusOnly = false,
  hideDropzone = false,
}: FileUploadDialogProps) {
  const { isMobile } = useDeviceDetection();
  const [canClick, setCanClick] = React.useState(true); // Start with true
  const [isFilePickerOpen, setIsFilePickerOpen] = React.useState(false);
  const [displayBusyPhase, setDisplayBusyPhase] = React.useState<'idle' | 'waiting' | 'processing'>('idle');
  const [isDragging, setIsDragging] = React.useState(false);
  const [dropWarning, setDropWarning] = React.useState<{ title: string; message: string } | null>(null);
  const suppressNextDropzoneClickRef = React.useRef(false);
  const awaitingProcessingAfterPickerRef = React.useRef(false);

  // Prevent immediate clicks when dialog opens (especially on mobile where touch events can bubble)
  React.useEffect(() => {
    if (isOpen) {
      // Don't block clicks anymore - the issue was affecting react-select
      setCanClick(true);
    }
  }, [isOpen]);

  // Reset file picker state when dialog closes
  React.useEffect(() => {
    if (!isOpen) {
      setIsFilePickerOpen(false);
      setDisplayBusyPhase('idle');
      awaitingProcessingAfterPickerRef.current = false;
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!isFilePickerOpen) return;

    const switchToProcessing = () => {
      awaitingProcessingAfterPickerRef.current = true;
      setDisplayBusyPhase('processing');
      setIsFilePickerOpen(false);
    };

    const handleWindowFocus = () => {
      switchToProcessing();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        switchToProcessing();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isFilePickerOpen]);

  const isProcessingFiles = !!awaitingFileSelection || uploadedFiles.some(f => f.status === 'uploading');
  const hasPendingProcessing = uploadedFiles.some(f => f.status === 'uploading');

  React.useEffect(() => {
    if (isFilePickerOpen) {
      setDisplayBusyPhase('waiting');
      return;
    }

    if (isProcessingFiles) {
      awaitingProcessingAfterPickerRef.current = false;
      setDisplayBusyPhase('processing');
      return;
    }

    if (!awaitingProcessingAfterPickerRef.current) {
      setDisplayBusyPhase('idle');
    }
  }, [isFilePickerOpen, isProcessingFiles]);

  React.useEffect(() => {
    if (isFilePickerOpen && uploadedFiles.some(f => f.status === 'uploading')) {
      setIsFilePickerOpen(false);
    }
  }, [isFilePickerOpen, uploadedFiles]);
  const isWaitingForFileSelection = displayBusyPhase === 'waiting';
  const isShowingBusyState = displayBusyPhase !== 'idle';
  const shouldShowProcessingState = statusOnly
    ? isOpen
    : isShowingBusyState;

  // Check if files are ready - for staged files (general chat), they're ready once no file is still processing.
  // For data analysis mode, files are ready when they have 'pending' status (ready to be uploaded)
  // or 'uploaded' status (already uploaded). They're NOT ready if 'uploading' or 'error'.
  const allFilesReady = uploadedFiles.length > 0 && 
    (isDataAnalysisMode 
      ? uploadedFiles.every(f => f.status === 'pending' || f.status === 'uploaded')
      : uploadedFiles.every(f => f.status !== 'uploading')
    );
  const hasFiles = uploadedFiles.length > 0;

  // Detect file type category from uploaded files
  const getFileTypeCategory = () => {
    if (uploadedFiles.length === 0) return null;
    
    const firstFile = uploadedFiles[0];
    const isImage = /\.(png|jpe?g)$/i.test(firstFile.name) || /image\/(png|jpeg)/.test(firstFile.type || '');
    const isData = /\.(csv|xlsx?|xlsm)$/i.test(firstFile.name) || 
                   ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(firstFile.type || '');
    const isText = /\.(pdf|docx?|pptx?|txt)$/i.test(firstFile.name) || 
                   ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'].includes(firstFile.type || '');
    
    if (isImage) return 'image';
    if (isData) return 'data';
    if (isText) return 'text';
    return null;
  };

  const fileTypeCategory = getFileTypeCategory();

  const handleUploadClick = () => {
    onUpload();
    // Don't call onClose() here - the upload handler will close the dialog
    // This way we can distinguish between successful upload and cancellation
  };

  const handleCancelClick = () => {
    // Cancel button can be pressed anytime - parent will handle cleanup
    onClose();
  };

  const handleDropzoneClick = () => {
    if (suppressNextDropzoneClickRef.current) {
      suppressNextDropzoneClickRef.current = false;
      return;
    }

    // Only allow clicks after the dialog has been open for a moment (prevents touch event bubbling on mobile)
    if (canClick) {
      setIsFilePickerOpen(true);
      onDropzoneClick();
    }
  };

  const handleCancelFilePickerOrRead = () => {
    suppressNextDropzoneClickRef.current = true;
    awaitingProcessingAfterPickerRef.current = false;
    setIsFilePickerOpen(false);
    setDisplayBusyPhase('idle');
    onCancelFileRead?.();
  };

  const handleBusyCancelMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleCancelFilePickerOrRead();
  };

  const handleBusyCancelClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleCancelFilePickerOrRead();
  };

  const renderProcessingState = () => {
    if (!shouldShowProcessingState) return null;

    const statusKey = !statusOnly && isWaitingForFileSelection ? 'waiting' : 'processing';

    return (
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.98 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        onMouseDown={handleBusyCancelMouseDown}
        onClick={handleBusyCancelClick}
        role="button"
        aria-label={!statusOnly && isWaitingForFileSelection ? 'Cancel file selection' : 'Cancel file processing'}
        className="mb-4 flex cursor-pointer flex-col gap-3 rounded-xl border border-[var(--color-accent)] bg-[var(--hover)] px-5 py-4 text-center"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={statusKey}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="flex flex-col items-center gap-3"
          >
            <div className="w-8 h-8 border-4 border-[var(--color-text)] border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <div className="min-w-0 text-center">
              <p className="text-base font-semibold text-[var(--color-text)]">
                {!statusOnly && isWaitingForFileSelection ? 'Waiting for file selection...' : 'Processing selected files...'}
              </p>
              <p className="text-sm text-[var(--color-text)] opacity-70">Click to cancel</p>
            </div>
            {(statusOnly || !isWaitingForFileSelection) && (
              <p className="text-sm text-[var(--color-text)] opacity-80">
                Any uploaded files may take a second to finish uploading. Please be patient for large files.
              </p>
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    );
  };

  const renderFileTypeInformation = () => {
    if (isShowingBusyState) return null;

    if (!isDataAnalysisMode && !hasFiles) {
      return (
        <>
          <div className="mb-4 p-3 bg-yellow-100 dark:bg-yellow-900 dark:bg-opacity-20 border border-yellow-600 dark:border-yellow-500 rounded-lg">
            <p className="text-sm text-gray-800 dark:text-[var(--color-text)]">
              <span className="font-semibold">Note:</span> You cannot mix file types. Choose all text documents, all images, or all data files.
            </p>
          </div>

          <div className="mb-6 text-sm text-[var(--color-text)]">
            <div className="space-y-3">
              <div className="flex gap-2 items-start">
                <span className="flex-shrink-0">•</span>
                <div>
                  <span className="font-medium">Text documents</span>
                  <span className="opacity-70"> (.docx, .txt, .pdf, .pptx)</span>
                  <span>: Up to 2 files, no more than 16MB per file. ~200 pages max</span>
                </div>
              </div>
              <div className="flex gap-2 items-start">
                <span className="flex-shrink-0">•</span>
                <div>
                  <span className="font-medium">Images</span>
                  <span className="opacity-70"> (.jpeg, .jpg, .png)</span>
                  <span>: Up to 5 files at a time, 5MB each</span>
                </div>
              </div>
              <div className="flex gap-2 items-start">
                <span className="flex-shrink-0">•</span>
                <div>
                  <span className="font-medium">Data Files</span>
                  <span className="opacity-70"> (.csv, .xlsx)</span>
                  <span>: Up to 2 files, no more than 16MB each</span>
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="mb-6 text-sm text-[var(--color-text)]">
          {fileTypeCategory === 'text' && (
            <div>
              <span className="font-medium">Text documents</span>
              <span className="opacity-70"> (.docx, .txt, .pdf, .pptx)</span>
              <span>: Up to 2 files, no more than 16MB per file. ~200 pages max</span>
            </div>
          )}

          {fileTypeCategory === 'image' && (
            <div>
              <span className="font-medium">Images</span>
              <span className="opacity-70"> (.jpeg, .jpg, .png)</span>
              <span>: Up to 5 files at a time, 5MB each</span>
            </div>
          )}

          {(fileTypeCategory === 'data' || isDataAnalysisMode) && (
            <div>
              <span className="font-medium">Data Files</span>
              <span className="opacity-70"> (.csv, .xlsx)</span>
              <span>: Up to 2 files, no more than 16MB each</span>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderUploadedFiles = () => {
    if (!hasFiles) return null;

    return (
      <div className="mb-6 p-4 border-2 border-[var(--color-accent)] rounded-lg bg-[var(--color-bg)] bg-opacity-5">
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-2">Selected Files:</h3>
        <div className="space-y-2">
          {uploadedFiles.map((file, index) => {
            const isFileProcessing = file.status === 'uploading';

            return (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center gap-2 p-2 bg-[var(--hover)] rounded-lg"
              >
                <span className="flex-1 text-sm text-[var(--color-text)] truncate">
                  {file.name}
                </span>
                {isFileProcessing && (
                  <span className="inline-flex items-center gap-2 text-xs text-[var(--color-accent)] whitespace-nowrap">
                    <span className="w-3.5 h-3.5 border-2 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </span>
                )}
                {!isFileProcessing && onRemoveFile && (
                  <button
                    onClick={() => onRemoveFile(file.name)}
                    className="p-1 hover:bg-[var(--border)] rounded transition-colors"
                    aria-label={`Remove ${file.name}`}
                  >
                    {React.createElement(MdClose as any, { className: "w-4 h-4 text-[var(--color-text)]" })}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the dropzone itself, not a child element
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    // --- Validate file types before dispatching to the hidden input ---
    const isDataMode = isDataAnalysisMode;

    const isAllowedExtension = (file: File): boolean => {
      const name = file.name.toLowerCase();
      const type = file.type;
      if (isDataMode) {
        // Data analysis: CSV and Excel only
        return /\.(csv|xlsx?|xlsm)$/i.test(name) ||
          ['text/csv', 'application/vnd.ms-excel',
           'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(type);
      }
      // General: images, text docs (.docx/.pptx only), data files (.xlsx/.csv only)
      return /\.(png|jpe?g|pdf|docx|pptx|txt|csv|xlsx)$/i.test(name) ||
        ['image/png', 'image/jpeg', 'application/pdf',
         'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
         'application/vnd.openxmlformats-officedocument.presentationml.presentation',
         'text/plain', 'text/csv',
         'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(type);
    };

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    Array.from(files).forEach(file => {
      if (isAllowedExtension(file)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(
          `${file.name}: ${isDataMode ? 'Unsupported type (CSV/Excel only)' : 'Unsupported file type'}`
        );
      }
    });

    if (invalidFiles.length > 0) {
      const legacyNote = isDataMode
        ? '\n\nNote: Legacy Excel format (.xls) is not supported — please use .xlsx.'
        : '\n\nNote: Legacy formats (.doc, .ppt, .xls) are not supported — please use .docx, .pptx, or .xlsx.';
      setDropWarning({
        title: 'File Selection Warning',
        message: invalidFiles.join('\n') + legacyNote,
      });
    }

    // Call parent handler directly with valid files — avoids broken DOM event dispatch
    if (validFiles.length > 0 && onFilesDropped) {
      onFilesDropped(validFiles);
    }
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const dialogVariants = isMobile
    ? {
        hidden: { y: '100%' },
        visible: { y: 0 }
      }
    : {
        hidden: { scale: 0.9 },
        visible: { scale: 1 }
      };

  const dialogContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-[10000]"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={(e) => {
              // Don't close if clicking on react-select menu
              if ((e.target as HTMLElement).closest('.select__menu')) {
                return;
              }
              handleCancelClick();
            }}
          />

          {/* Dialog - Mobile: Full screen, Desktop: Centered with flexbox */}
          {isMobile ? (
            <motion.div
              className="fixed inset-0 z-[10001] bg-[var(--color-bg)]"
              style={{ backgroundColor: 'var(--color-bg)', opacity: 1 }}
              variants={dialogVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              {/* Close button for mobile */}
              <div className="flex justify-end p-4">
                <button
                  onClick={handleCancelClick}
                  className="p-2 rounded-full hover:bg-[var(--hover)] transition-colors"
                  aria-label="Close"
                >
                  {React.createElement(MdClose as any, { className: "w-6 h-6 text-[var(--color-text)]" })}
                </button>
              </div>

              {/* Content */}
              <div className="p-6 pt-2">
                {statusOnly || hideDropzone ? null : (
                  <FileDropZone
                    isBusy={isShowingBusyState}
                    isDragging={isDragging}
                    label="Select files"
                    onDropzoneClick={handleDropzoneClick}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  />
                )}

                {renderProcessingState()}

                {!statusOnly && (
                  <>
                    {/* Data Analysis requirement note */}
                    {isDataAnalysisMode && (
                      <div className="mb-4 p-3 bg-blue-500 bg-opacity-10 border border-blue-500 rounded-lg">
                        <p className="text-sm text-[var(--color-text)]">
                          <span className="font-semibold">Note:</span> The data analysis tool requires files to be attached. Please select your data files to continue.
                        </p>
                      </div>
                    )}

                    {renderFileTypeInformation()}
                  </>
                )}

                {/* File Previews - Show uploaded files */}
                {renderUploadedFiles()}

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={handleCancelClick}
                    className="px-4 py-2 rounded-lg border border-[var(--border)] text-[var(--color-text)] hover:bg-[var(--hover)] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUploadClick}
                    disabled={!allFilesReady || !hasFiles || isShowingBusyState}
                    className="px-4 py-2 rounded-lg bg-[var(--color-accent)] text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isShowingBusyState || isUploading ? 'Processing...' : 'Upload File(s)'}
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            <div className="fixed inset-0 z-[10001] flex items-center justify-center pointer-events-none">
              <motion.div
                className="bg-[var(--color-bg)] rounded-lg shadow-xl border border-[var(--border)] w-[90vw] max-w-[500px] pointer-events-auto"
                style={{ backgroundColor: 'var(--color-bg)', opacity: 1 }}
                variants={dialogVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                {/* Content */}
                <div className="p-6">
                  {statusOnly || hideDropzone ? null : (
                    <FileDropZone
                      isBusy={isShowingBusyState}
                      isDragging={isDragging}
                      label="Drag and drop or select files"
                      onDropzoneClick={handleDropzoneClick}
                      onDragEnter={handleDragEnter}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    />
                  )}

                  {renderProcessingState()}

                  {!statusOnly && (
                    <>
                      {/* Data Analysis requirement note */}
                      {(isDataAnalysisMode) && (
                        <div className="mb-4 p-3 bg-blue-500 bg-opacity-10 border border-blue-500 rounded-lg">
                          <p className="text-sm text-[var(--color-text)]">
                            <span className="font-semibold">Note:</span> The data analysis tool requires files to be attached. Please select your data files to continue.
                          </p>
                        </div>
                      )}

                      {renderFileTypeInformation()}
                    </>
                  )}

                  {/* File Previews - Show uploaded files */}
                  {renderUploadedFiles()}

                  {/* Action Buttons */}
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={handleCancelClick}
                      className="px-4 py-2 rounded-lg border border-[var(--border)] text-[var(--color-text)] hover:bg-[var(--hover)] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUploadClick}
                      disabled={!allFilesReady || !hasFiles || isShowingBusyState}
                      className="px-4 py-2 rounded-lg bg-[var(--color-accent)] text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isShowingBusyState || isUploading ? 'Processing...' : 'Upload File(s)'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </>
      )}
    </AnimatePresence>
  );

  // File Selection Warning popup — rendered above the dialog
  const activeWarning = dropWarning ?? (fileWarning ? { title: fileWarningTitle ?? 'File Selection Warning', message: fileWarning } : null);
  const warningPopup = activeWarning
    ? <FileSelectionWarningDialog
        title={activeWarning.title}
        message={activeWarning.message}
        zIndex={20000}
        onClose={() => {
          if (dropWarning) setDropWarning(null);
          else onClearFileWarning?.();
        }}
      />
    : null;

  // Render in a portal to escape any parent stacking contexts
  return <>{createPortal(dialogContent, document.body)}{warningPopup}</>;
}
